
import { useState, useEffect, useCallback } from "react";
import { socialApi, SocialCounts } from "@/api/social";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Core hook for social features that handles basic state and data fetching
 */
export const useSocialCore = (
  derivativeId: string,
  initialCounts?: {
    likes_count?: number;
    favorites_count?: number;
    comments_count?: number;
  }
) => {
  const { isAuthenticated, user } = useAuth();
  const [counts, setCounts] = useState<SocialCounts>({
    likes_count: initialCounts?.likes_count || 0,
    favorites_count: initialCounts?.favorites_count || 0,
    comments_count: initialCounts?.comments_count || 0
  });
  
  // OPTIMIZATION: Don't show loading state if initialCounts is provided
  const [isLoading, setIsLoading] = useState(!initialCounts);
  const [isAcademy, setIsAcademy] = useState(false);
  
  // Check if the current user is an Academy member
  useEffect(() => {
    const checkAcademyStatus = async () => {
      if (user) {
        try {
          const { data } = await socialApi.getSupabase()
            .from('profiles')
            .select('is_academy')
            .eq('id', user.id)
            .single();
          
          setIsAcademy(!!data?.is_academy);
        } catch (error) {
          console.error("Error checking academy status:", error);
          setIsAcademy(false);
        }
      } else {
        setIsAcademy(false);
      }
    };
    
    checkAcademyStatus();
  }, [user]);

  // Fetch initial counts if not provided
  useEffect(() => {
    const fetchCounts = async () => {
      if (!derivativeId || initialCounts) return;
      
      setIsLoading(true);
      
      try {
        const countsData = await socialApi.getSocialCounts(derivativeId);
        setCounts(countsData);
      } catch (error) {
        console.error(`Error fetching social counts for derivative ${derivativeId}:`, error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCounts();
  }, [derivativeId, initialCounts]);

  // Update count utility function
  const updateCount = useCallback((type: keyof SocialCounts, increment: boolean) => {
    setCounts(prev => ({
      ...prev,
      [type]: Math.max(0, (prev[type] || 0) + (increment ? 1 : -1))
    }));
  }, []);

  return {
    isAuthenticated,
    user,
    isAcademy,
    counts,
    setCounts,
    isLoading,
    updateCount,
    canComment: isAuthenticated && isAcademy
  };
};
